import {Component, EventEmitter, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {BaseComponent} from '../../core/base.component';
import {HeaderService} from '../../core/services/header.service';
import {Title} from '@angular/platform-browser';
import {AuthService} from '../../core/security/auth.service';
import {ReportsService} from '../reports.service';
import {ActivityDashboardV2Service} from './activity-dashboard-v2.service';
import {appColors} from '../../core/styles/colors';
import {DateTime, Settings} from 'luxon';
import {SelectedFilterValuesModel} from '../../dialogs/grid-filter/selected-filter-values.model';
import {FilterOptionModel} from '../../dialogs/grid-filter/filter-option.model';
import {DateRangeService} from '../../core/services/date-range.service';
import {MatDialog} from '@angular/material/dialog';
import {ActivityDashboardResponse} from '../../api/models/activity-dashboard-response';
import {AdGrandTotalModel} from '../../api/models/ad-grand-total-model';
import * as _ from 'lodash';
import {CallLogsV2DialogComponent} from '../call-logs-v2/call-logs-v2-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { NgIf, NgFor } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DashboardCardComponent } from '../shared/dashboard-card.component';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { ActivityChartByTimeV2Component } from './charts/activity-chart-by-time-v2.component';
import { ActivityBarChartV2Component } from './charts/activity-bar-chart-v2.component';


@Component({
    selector: 'app-activity-dashboard-v2',
    templateUrl: './activity-dashboard-v2.component.html',
    styleUrls: ['./activity-dashboard-v2.component.scss'],
    standalone: true,
    imports: [FlexModule, NgIf, MatProgressBar, GridFilterComponent, FilterRowAdditionalContentComponent, MatButton, MatIcon, DashboardCardComponent, MatCard, MatCardContent, ActivityChartByTimeV2Component, ActivityBarChartV2Component, MatCardTitle, NgFor]
})
export class ActivityDashboardV2Component extends BaseComponent implements OnInit, OnDestroy {

  filterChangeEvent = new EventEmitter<any>();
  selectedFilterChange = new EventEmitter<SelectedFilterValuesModel[]>();
  selectedFilter: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  filterOptions: FilterOptionModel[] = [];
  additionalFilter: any = {};

  colors = appColors;
  dashboardData: ActivityDashboardResponse;

  grandTotals: AdGrandTotalModel = {} as AdGrandTotalModel;
  topByGroups: any = [];
  topByUsers: any = [];
  timeSeriesType: 'line' | 'bar' = 'line';
  timeSeriesTotals: any = [];
  pieChartData: any = [];
  pieChartTimeData: any = [];

  defaultDateRange = 0;

  isLoading = true;
  isLoadingGrandTotals = true;
  isLoadingTimeSeriesTotals = true;
  isLoadingTopByGroupTotals = true;
  isLoadingTopByAgentTotals = true;

  constructor(public reportsService: ReportsService,
              public activityDashboardService: ActivityDashboardV2Service,
              public dateRangeService: DateRangeService,
              public dialog: MatDialog,
              route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService) {
    super(route, headerService, titleService, authService);
  }

  ngOnInit() {
    super.ngOnInit();
    console.log('my on init');
    this.defaultDateRange = this.dateRangeService.selectedDateRange.action || 0;

    if (this.reportsService.accountsLookup?.length > 0) {
      this.initFilterOptions();
    } else {
      this.reportsService.lookupUpdatedEvent.subscribe(() => {
        this.initFilterOptions();
      });
    }

    this.filterChangeEvent
      .subscribe(async (filter) => {
        this.additionalFilter = filter;
        console.log(filter);

        this.getDashboardGrandTotals();
        this.getDashboardTimeSeriesTotals();
        this.getDashboardTopByGroupTotals();
        this.getDashboardTopByAgentTotals();
      });
  }

  getDashboardGrandTotals() {
    this.isLoadingGrandTotals = true;
    this.activityDashboardService.getDashboardGrandTotals(
      Object.assign({
        domain: this.reportsService.selectedDomain
      }, this.additionalFilter || {})
    ).then(data => {
      this.grandTotals = data.grandTotal || {
        group: '',
        name: '',
        totalAnsweredCalls: 0,
        totalIncoming: 0,
        totalMailbox: 0,
        totalMissed: 0,
        totalOutgoing: 0,
        totalTalkDuration: 0,
        totalRingDuration: 0,
        totalHoldDuration: 0,
        avgTalkDuration: 0,
        avgRingDuration: 0,
        avgHoldDuration: 0,
      };

      this.pieChartData = [
        {
          calls: this.grandTotals.totalIncoming,
          color: appColors.primaryGreen,
          extension: 'Incoming Calls',
          isVisible: true,
        }, {
          calls: this.grandTotals.totalOutgoing,
          color: appColors.primaryBlue,
          extension: 'Outgoing Calls',
          isVisible: true,
        }, {
          calls: this.grandTotals.totalMissed,
          color: appColors.primaryRed,
          extension: 'Missed Calls',
          isVisible: true,
        }, {
          calls: this.grandTotals.totalMailbox,
          color: appColors.orange,
          extension: 'Mailbox Calls',
          isVisible: true,
        }
      ];

      this.pieChartTimeData = [
        {
          totalTime: this.grandTotals.totalTalkDuration,
          avgTime: this.grandTotals.avgTalkDuration,
          color: appColors.primaryGreen,
          extension: 'Talk Time',
        }, {
          totalTime: this.grandTotals.totalRingDuration,
          avgTime: this.grandTotals.avgRingDuration,
          color: appColors.orange,
          extension: 'Ring Time',
        }, {
          totalTime: this.grandTotals.totalHoldDuration,
          avgTime: this.grandTotals.avgHoldDuration,
          color: appColors.primaryRed,
          extension: 'Hold Time',
        }
      ];
    }).finally(() => {
      this.isLoadingGrandTotals = false;
    });
  }

  getDashboardTimeSeriesTotals() {
    this.isLoadingTimeSeriesTotals = true;
    this.activityDashboardService.getDashboardTimeSeriesTotals(
      Object.assign({
        domain: this.reportsService.selectedDomain
      }, this.additionalFilter || {})
    ).then(data => {

      let maxDate = DateTime.fromISO('1790-01-01');
      this.timeSeriesTotals = data.timeSeriesTotals.map((item) => {
        const localDateTime =
          DateTime
            .fromISO(item.group)
            .setZone(Settings.defaultZone);

        if (localDateTime > maxDate) {
          maxDate = localDateTime;
        }

        return {
          date: localDateTime.toJSDate(),
          dateStr: localDateTime.toMillis(),
          isIncoming: item.totalIncoming,
          isOutgoing: item.totalOutgoing,
          isMissed: item.totalMissed,
          isMailbox: item.totalMailbox,
        };
      });
    }).finally(() => {
      this.isLoadingTimeSeriesTotals = false;
    });
  }

  getDashboardTopByGroupTotals() {
    this.isLoadingTopByGroupTotals = true;
    this.activityDashboardService.getDashboardTopByGroupTotals(
      Object.assign({
        domain: this.reportsService.selectedDomain
      }, this.additionalFilter || {})
    ).then(data => {
      this.topByGroups = data.topByGroup.map((item) => {
        return {
          number: item.group,
          totalCount: item.totalIncoming,
          percentValue: item.totalIncoming * 100 / this.grandTotals.totalIncoming
        };
      });
    }).finally(() => {
      this.isLoadingTopByGroupTotals = false;
    });
  }

  getDashboardTopByAgentTotals() {
    this.isLoadingTopByAgentTotals = true;
    this.activityDashboardService.getDashboardTopByAgentTotals(
      Object.assign({
        domain: this.reportsService.selectedDomain
      }, this.additionalFilter || {})
    ).then(data => {
      this.topByUsers = data.topByUser.map((item) => {
        return {
          number: item.group,
          totalCount: item.totalIncoming,
          percentValue: item.totalIncoming * 100 / this.grandTotals.totalIncoming,
          outTotalCount: item.totalOutgoing,
          outPercentValue: item.totalOutgoing * 100 / this.grandTotals.totalOutgoing,
          totalAllCount: item.totalIncoming + item.totalOutgoing,
          percentAllValue: item.totalIncoming * 100 / (item.totalIncoming + item.totalOutgoing),
        };
      });
    }).finally(() => {
      this.isLoadingTopByAgentTotals = false;
    });
  }

  mapData() {
    this.dashboardData = this.activityDashboardService.dashboardData;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  onSelectedItemsChanged($event: SelectedFilterValuesModel[]) {
    this.selectedFilter = $event;
  }

  onFilterChanged(filter: any) {
    let extensions = [];
    let accounts = [];

    if (filter['extensions']) {
      extensions = _.union(extensions, filter['extensions']);
      filter['extensions'] = undefined;
    }
    if (filter['departments']) {
      const departments = this.reportsService.getDepartmentsExtensions(filter['departments']);
      extensions = _.union(extensions, departments);
      filter['departments'] = undefined;
    }
    if (filter['agentGroup']) {
      accounts = _.union(accounts, filter['agentGroup']);
      filter['agentGroup'] = undefined;
    }
    if (filter['huntGroup']) {
      accounts = _.union(accounts, filter['huntGroup']);
      filter['huntGroup'] = undefined;
    }
    if (filter['attendants']) {
      accounts = _.union(accounts, filter['attendants']);
      filter['attendants'] = undefined;
    }
    if (filter['durationTo']) {
      filter['durationTo'] = filter['durationTo'] * 1000;
    }
    if (filter['durationFrom']) {
      filter['durationFrom'] = filter['durationFrom'] * 1000;
    }

    filter['extensions'] = extensions;
    filter['groupAccounts'] = accounts;

    this.filterChangeEvent.emit(filter);
  }

  initFilterOptions(): void {
    this.filterOptions = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any'
      },
      {
        filterKey: 'durationFrom',
        multiValue: false,
        showAlways: true,
        title: 'Duration From',
        type: 'number',
      },
      {
        filterKey: 'durationTo',
        multiValue: false,
        showAlways: true,
        title: 'Duration To',
        type: 'number',
      },
      {
        filterKey: 'nameFrom',
        multiValue: false,
        showAlways: true,
        title: 'From Name',
        type: 'string',
      },
      {
        filterKey: 'nameTo',
        multiValue: false,
        showAlways: true,
        title: 'To Name',
        type: 'string',
      },
      {
        filterKey: 'numberFrom',
        multiValue: false,
        showAlways: true,
        title: 'From Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'numberTo',
        multiValue: false,
        showAlways: true,
        title: 'To Number',
        type: 'phoneNumber',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Answered',
        value: 'Answered',
        searchText: 'Call Result: Answered',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Missed',
        value: 'Missed',
        searchText: 'Call Result: Missed',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Hung Up',
        value: 'Hung Up',
        searchText: 'Call Result: Hung Up',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Mailbox',
        value: 'Mailbox',
        searchText: 'Call Result: Mailbox',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Connected',
        value: 'Connected',
        searchText: 'Call Result: Connected',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Not Connected',
        value: 'Connected',
        searchText: 'Call Result: Not Connected',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Login',
        value: 'Login',
        searchText: 'Call Result: Login',
      },
      {
        filterKey: 'statuses',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Result',
        displayValue: 'Logout',
        value: 'Logout',
        searchText: 'Call Result: Logout',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Type',
        displayValue: 'Incoming',
        value: 'isIncoming',
        searchText: 'Call Type: Incoming',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Type',
        displayValue: 'Outgoing',
        value: 'isOutgoing',
        searchText: 'Call Type: Outgoing',
      },
      {
        filterKey: 'types',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Call Type',
        displayValue: 'Internal',
        value: 'isLocal',
        searchText: 'Call Type: Internal',
      },
      {
        filterKey: 'exclude',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Exclude',
        displayValue: 'Agent Groups',
        value: 'isGroup',
        searchText: 'Exclude: Agent Groups',
      },
      {
        filterKey: 'exclude',
        multiValue: true,
        showAlways: false,
        type: 'static',
        title: 'Exclude',
        displayValue: 'Hunt Groups',
        value: 'isHunt',
        searchText: 'Exclude: Hunt Groups',
      },
    ];

    if (this.reportsService.extensionsLookup.length > 0) {
      for (const item of this.reportsService.extensionsLookup) {
        this.filterOptions.push({
          filterKey: 'extensions',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'User',
          displayValue: item.text,
          value: item.value,
          searchText: `User: ${item.text}`,
        });
      }
    }

    if (this.reportsService.departmentsLookup.length > 0) {
      for (const item of this.reportsService.departmentsLookup) {
        this.filterOptions.push({
          filterKey: 'departments',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Department',
          displayValue: item.text,
          value: item.value,
          searchText: `Department: ${item.text}`,
        });
      }
    }

    if (this.reportsService.groupsLookup.length > 0) {
      for (const item of this.reportsService.groupsLookup) {
        this.filterOptions.push({
          filterKey: 'agentGroup',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Agent Group',
          displayValue: item.text,
          value: item.value,
          searchText: `Agent Group: ${item.text}`,
        });
      }
    }

    if (this.reportsService.huntGroupsLookup.length > 0) {
      for (const item of this.reportsService.huntGroupsLookup) {
        this.filterOptions.push({
          filterKey: 'huntGroup',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Hunt Group',
          displayValue: item.text,
          value: item.value,
          searchText: `Hunt Group: ${item.text}`,
        });
      }
    }

    if (this.reportsService.attendantsLookup.length > 0) {
      for (const item of this.reportsService.attendantsLookup) {
        this.filterOptions.push({
          filterKey: 'attendants',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Attendant',
          displayValue: item.text,
          value: item.value,
          searchText: `Attendant: ${item.text}`,
        });
      }
    }

    if (this.reportsService.domainsLookup.length > 0) {
      for (const item of this.reportsService.domainsLookup) {
        this.filterOptions.push({
          filterKey: 'domains',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Company',
          displayValue: item.text,
          value: item.value,
          searchText: `Company: ${item.text}`,
        });
      }
    }
  }

  onRefreshClicked() {
    this.filterChangeEvent.emit(this.additionalFilter);
  }

  openCallLogs(type?: string, status?: string, group?: string, user?: string) {
    console.log('open call logs dialog');
    let statuses = undefined;
    if (status) {
      statuses = [status];
    }
    let types = undefined;
    if (type) {
      types = [type];
    }

    let groupAccounts = undefined;
    if (group) {
      groupAccounts = [group];
    }

    let extensions = undefined;
    if (user) {
      extensions = [user];
      types = ['isIncoming', 'isOutgoing'];
    }

    this.dialog.open(CallLogsV2DialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        filter: {
          types: types,
          statuses: statuses,
          groupAccounts: groupAccounts,
          extensions: extensions,
          excludeStatuses: ['Login', 'Logout', 'Auto Logout'],
          startDate: this.additionalFilter.startDate,
          endDate: this.additionalFilter.endDate,
          domain: this.reportsService.selectedDomain
        }
      }
    });
  }
}
